import React, { useEffect, useState } from 'react';
import { LineItem } from '@Types/wishlist/LineItem';
import classNames from 'classnames';
import { useWishlist } from '@Provider/frontastic';
import HeartIcon from 'components/icons/heart';
import HeartFilledIcon from 'components/icons/heart-filled';
import { useFormat } from 'helpers/hooks/useFormat';
import styles from './button.module.scss';
import { usePlausible } from 'next-plausible';

export interface IWishlistButton {
  sku: string;
  onAddToWishlist?: () => void;
}

export const WishlistButton: React.FC<IWishlistButton> = ({ sku, onAddToWishlist }) => {
  const { formatMessage } = useFormat({ name: 'wishlist' });
  const { data: wishlist, addToWishlist, removeLineItem } = useWishlist();
  const plausible = usePlausible();
  const [productInWishlist, setProductInWishlist] = useState<LineItem>(null);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);

  useEffect(() => {
    if (wishlist?.lineItems?.length) {
      const item = wishlist.lineItems.find(({ variant }) => variant.sku === sku);

      setProductInWishlist(item ?? null);
    } else {
      setProductInWishlist(null);
    }
  }, [wishlist?.lineItems, sku]);

  const animateButton = () => {
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handleToggleWishlist = async () => {
    if (productInWishlist) {
      await removeLineItem(productInWishlist.lineItemId);
    } else {
      await addToWishlist(sku, 1);
      plausible('addToWishlist');

      onAddToWishlist?.();
    }
    animateButton();
  };

  return (
    <button
      onClick={handleToggleWishlist}
      className={classNames(
        'flex shrink-0 items-center justify-center rounded-full transition duration-150 ease-out hover:bg-primary-100 focus:bg-primary-100',
        productInWishlist ? 'bg-primary-200' : 'bg-neutral-200',
        {
          [styles.animateHeartbeat]: isAnimating,
        },
      )}
      style={{ height: '51px', width: '51px' }}
      aria-pressed={!!productInWishlist}
      aria-label={
        productInWishlist ? formatMessage({ id: 'wishlist.remove' }) : formatMessage({ id: 'wishlist.add.to.bag' })
      }
    >
      {productInWishlist ? <HeartFilledIcon aria-hidden="true" /> : <HeartIcon aria-hidden="true" />}
    </button>
  );
};
